import type { AdminChatThread } from "../types";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRouter, useSearch } from "@tanstack/react-router";
import { format, formatDistanceToNow } from "date-fns";
import { useEffect, useMemo, useRef } from "react";

export const AdminChatThreadList: React.FC<{
  threads: AdminChatThread[];
  onSelectThread: (thread: AdminChatThread) => void;
  selectedThread: AdminChatThread | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}> = ({
  threads,
  onSelectThread,
  selectedThread,
  searchQuery,
  onSearchChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
}) => {
  const search = useSearch({ strict: false });
  const router = useRouter();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore || !onLoadMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 1.0 },
    );

    const current = loadMoreRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [hasMore, onLoadMore, isLoadingMore]);

  const handleTabChange = (value: string) => {
    router.navigate({
      // @ts-expect-error - existing dynamic route typing
      search: (prev) => ({
        ...prev,
        archived: value === "archived",
      }),
    });
  };

  const titleForThread = (thread: AdminChatThread) =>
    [
      ...thread.organization_participants
        .map((participant) => participant.name ?? "Unknown Organization")
        .filter(Boolean),
      ...thread.user_participants
        .map((participant) =>
          `${participant.first_name ?? ""} ${participant.last_name ?? ""}`.trim(),
        )
        .filter((name) => name.length > 0),
    ].join(", ");

  const getInitials = (thread: AdminChatThread) => {
    const primary = thread.user_participants[0];
    if (primary?.first_name || primary?.last_name) {
      return (
        `${primary.first_name?.[0] ?? ""}${primary.last_name?.[0] ?? ""}`.toUpperCase() ||
        "C"
      );
    }
    const org = thread.organization_participants[0];
    return org?.name?.slice(0, 2).toUpperCase() ?? "C";
  };

  const getSnippet = (thread: AdminChatThread) => {
    if (thread.last_message?.type === "job-offer") {
      return "Job offer shared within this conversation.";
    }
    if (thread.last_message?.type === "job-offer-proposal") {
      return "Changes proposed for a job offer.";
    }
    if (thread.last_message?.type === "job-offer-shifts-canceled") {
      return "Shifts were canceled on a job offer.";
    }
    if (thread.last_message?.type === "credential-edit-request") {
      return "Credential edit access requested.";
    }
    if (thread.last_message?.type === "form-assignment") {
      return "A form was sent to the provider.";
    }
    return thread.last_message?.text ?? "No recent messages yet.";
  };

  const getOrganizations = (thread: AdminChatThread) =>
    thread.organization_participants
      .map((participant) => participant.name ?? "Unknown Organization")
      .filter(Boolean)
      .join(" • ") || "Unassigned";

  const archivedLabel = useMemo(
    () => (search.archived ? "Archived" : "Active"),
    [search.archived],
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <div className="shrink-0 border-b border-slate-200 bg-white p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs
            defaultValue={search.archived ? "archived" : "active"}
            onValueChange={handleTabChange}
          >
            <TabsList className="grid h-10 grid-cols-2 rounded-lg border border-slate-200 bg-slate-100 p-0.5">
              <TabsTrigger
                value="active"
                className="h-8 min-w-0 rounded-md px-3 py-1 text-sm font-medium text-slate-600 focus-visible:ring-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-xs data-[state=inactive]:hover:bg-white data-[state=inactive]:hover:text-slate-900"
              >
                Active
              </TabsTrigger>
              <TabsTrigger
                value="archived"
                className="h-8 min-w-0 rounded-md px-3 py-1 text-sm font-medium text-slate-600 focus-visible:ring-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-xs data-[state=inactive]:hover:bg-white data-[state=inactive]:hover:text-slate-900"
              >
                Archived
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="text-xs text-slate-500">
            {archivedLabel}: {threads.length} thread
            {threads.length === 1 ? "" : "s"}
          </span>
        </div>
        <Input
          aria-label="Search participants or message text"
          placeholder="Search participants or message text..."
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          className="mt-3 w-full"
        />
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
        {threads.map((thread) => {
          const isSelected = selectedThread?.id === thread.id;
          const unreadCount =
            (thread as { unread_count?: number }).unread_count ?? 0;
          const initials = getInitials(thread);
          const title = titleForThread(thread);
          const organizations = getOrganizations(thread);
          const snippet = getSnippet(thread);
          const formattedTimestamp = thread.last_message_time
            ? format(new Date(thread.last_message_time), "MMM d, yyyy, h:mm a")
            : "No timestamp";
          const relativeTimestamp = thread.last_message_time
            ? `${formatDistanceToNow(new Date(thread.last_message_time), {
                addSuffix: true,
              })}`
            : "No activity";

          return (
            <button
              key={thread.id}
              type="button"
              aria-pressed={isSelected}
              className={`block w-full min-w-0 rounded-none border-b border-l-4 border-slate-200 px-4 py-4 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
                isSelected
                  ? "border-l-primary bg-primary/5"
                  : "border-l-transparent bg-white hover:bg-slate-50"
              }`}
              onClick={() => onSelectThread(thread)}
            >
              <div className="flex min-w-0 items-start gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-semibold ${
                    isSelected
                      ? "bg-primary/10 text-primary"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <h3
                      title={title}
                      className={`min-w-0 flex-1 truncate text-sm font-semibold leading-5 ${
                        isSelected ? "text-primary" : "text-slate-900"
                      }`}
                    >
                      {title}
                    </h3>
                    <span
                      title={formattedTimestamp}
                      className="shrink-0 whitespace-nowrap text-[11px] leading-5 text-slate-500"
                    >
                      {relativeTimestamp}
                    </span>
                  </div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-2">
                    <p
                      title={organizations}
                      className="min-w-0 flex-1 truncate text-xs leading-5 text-slate-500"
                    >
                      {organizations}
                    </p>
                    {thread.archived_at && (
                      <Badge
                        variant="outline"
                        className="shrink-0 rounded-md border-slate-200 bg-slate-50 px-1.5 py-0 text-[10px] leading-4 text-slate-600"
                      >
                        Archived
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1 flex min-w-0 items-center gap-2">
                    <p
                      title={snippet}
                      className="min-w-0 flex-1 truncate text-sm leading-5 text-slate-600"
                    >
                      {snippet}
                    </p>
                    {unreadCount > 0 && (
                      <Badge
                        variant="default"
                        className="shrink-0 rounded-md bg-primary px-1.5 py-0 text-[10px] leading-4 text-primary-foreground"
                      >
                        {unreadCount} new
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
        {hasMore && (
          <div
            ref={loadMoreRef}
            className="border-t py-3 text-center text-xs text-slate-500"
          >
            {isLoadingMore ? "Loading…" : "Scroll to load more"}
          </div>
        )}
        {!isLoadingMore && threads.length === 0 && (
          <div className="flex h-full items-center justify-center px-4 py-10 text-sm text-slate-500">
            No conversations match the current filters.
          </div>
        )}
      </div>
    </div>
  );
};
